import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  useScrollToTop();
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-200">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-400 hover:text-primary-300 whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料社区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliaoshequ.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        publishedTime={article.publishedAt}
        modifiedTime={article.publishedAt}
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>

          <article>
            <header className="mb-8">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-100 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 bg-secondary-100/50 text-secondary-400 text-xs font-medium rounded-full hover:bg-secondary-200/50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className={`${category.icon} mr-1`}></i>
                    {category.name}
                  </Link>
                )}
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  <i className="ri-user-line mr-1"></i>{article.author}
                </span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  <i className="ri-time-line mr-1"></i>{article.publishedAt}
                </span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  <i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万阅读
                </span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  <i className="ri-chat-3-line mr-1"></i>{article.comments}评论
                </span>
              </div>

              <div className="relative w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden mb-6">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </header>

            <div
              className="prose prose-invert max-w-none mb-10 text-foreground-300 leading-relaxed text-sm md:text-base"
              style={{ lineHeight: '2' }}
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-8 pt-6 border-t border-background-300/60">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-background-200/50 text-foreground-500 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-6 border-t border-b border-background-300/60 mb-10">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500 whitespace-nowrap">分享至：</span>
                <a
                  href="#"
                  className="w-9 h-9 flex items-center justify-center bg-background-200 rounded-full text-foreground-500 hover:bg-primary-500/20 hover:text-primary-400 transition-colors cursor-pointer"
                  aria-label="分享到微博"
                >
                  <i className="ri-weibo-line"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 flex items-center justify-center bg-background-200 rounded-full text-foreground-500 hover:bg-primary-500/20 hover:text-primary-400 transition-colors cursor-pointer"
                  aria-label="分享到微信"
                >
                  <i className="ri-wechat-line"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 flex items-center justify-center bg-background-200 rounded-full text-foreground-500 hover:bg-primary-500/20 hover:text-primary-400 transition-colors cursor-pointer"
                  aria-label="复制链接"
                >
                  <i className="ri-link"></i>
                </a>
              </div>

              <span className="text-xs text-foreground-600 whitespace-nowrap">
                声明：本文内容来源于网络爆料，不代表本站立场
              </span>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-stack-line text-accent-500 text-lg"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-200">
                  相关爆料
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group flex gap-4 p-4 bg-background-100 border border-background-300/60 rounded-xl hover:border-primary-500/30 transition-all cursor-pointer"
                  >
                    <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-2 leading-relaxed mb-1.5">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-2">
                        {ra.excerpt}
                      </p>
                      <span className="text-xs text-foreground-600 whitespace-nowrap">
                        <i className="ri-time-line mr-1"></i>{ra.publishedAt}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}